// src/components/resources/tools/generators/ColorPalette/palette-export.ts
//
// Pure palette-export serialization harvested verbatim from
// ColorPaletteGenerator.tsx (A11/C1). Turns a generated palette into the
// requested output format (JSON, CSS/SCSS/Tailwind vars, Figma/Sketch, raw
// hex/rgb/hsl). Framework-free and unit-tested.
import { formatRgbString, formatHslString } from '~/lib/utils/colors'
import type { ColorFormat, ColorInfo, ExportConfig } from './types'

/**
 * Serialize a palette to the requested {@link ColorFormat}.
 *
 * @param colors - The generated colors
 * @param format - Target output format
 * @param config - Export options (metadata/analysis inclusion, custom prefix)
 * @returns The serialized palette string
 */
export function exportPalette(colors: ColorInfo[], format: ColorFormat, config: ExportConfig): string {
  const prefix = config.customPrefix ?? 'color'

  switch (format) {
    case 'json':
      return JSON.stringify(colors.map(c => ({
        name: c.name ?? `Color ${colors.indexOf(c) + 1}`,
        hex: c.hex,
        rgb: c.rgb,
        hsl: c.hsl,
        ...(config.includeMetadata && c.metadata ? { metadata: c.metadata } : {}),
        ...(config.includeAnalysis ? {
          contrast: c.contrast,
          wcag: c.wcag,
          temperature: c.temperature,
          isDark: c.isDark,
          luminance: c.luminance,
        } : {}),
      })), null, 2)

    case 'css':
      return colors.map((c, i) =>
        `  --${prefix}-${i + 1}: ${c.hex};`
      ).join('\n')

    case 'scss':
      return colors.map((c, i) =>
        `$${prefix}-${i + 1}: ${c.hex};`
      ).join('\n')

    case 'tailwind':
      return `colors: {\n${colors.map((c, i) =>
        `  '${prefix}-${i + 1}': '${c.hex}',`
      ).join('\n')}\n}`

    case 'figma':
      return colors.map((c, i) =>
        `${prefix}-${i + 1}: ${c.hex}`
      ).join('\n')

    case 'sketch':
      return colors.map((c, i) =>
        `"${prefix}-${i + 1}": "${c.hex}"`
      ).join(',\n')

    case 'rgb':
      return colors.map(c => formatRgbString(c.rgb)).join('\n')

    case 'hsl':
      return colors.map(c => formatHslString(c.hsl)).join('\n')

    case 'hex':
    default:
      return colors.map(c => c.hex).join('\n')
  }
}
